import React, { useState, useEffect } from 'react';
import {
    Truck,
    Plus,
    ChevronLeft,
    MoreVertical,
    Eye,
    Edit,
    Trash2,
    Mail,
    Phone,
    MapPin,
    Search,
    Filter,
    Download,
    User,
    CheckCircle2,
    XCircle,
    Package,
    Loader2,
    AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent
} from '../../components/ui/dialog';
import api from '../../utils/api';
import { toast } from 'sonner';
import { cn } from '../../components/ui/utils';
import AddSupplierModal from './AddSupplierModal';

interface SuppliersProps {
    onBack: () => void;
}

const Suppliers: React.FC<SuppliersProps> = ({ onBack }) => {
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const data = await api.get('/api/inventory/suppliers');
            setSuppliers(data);
        } catch (err) {
            console.error(err);
            toast.error('Failed to fetch suppliers');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (supplier: any) => {
        setSelectedSupplier(supplier);
        setIsAddModalOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedSupplier) return;
        try {
            await api.delete(`/api/inventory/suppliers/${selectedSupplier.id}`);
            toast.success('Supplier deleted successfully');
            fetchData();
            setIsDeleteModalOpen(false);
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete supplier');
        }
    };

    const filteredSuppliers = suppliers.filter(s =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.contactName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getInitials = (name: string) => {
        if (!name) return '??';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    return (
        <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onBack}
                        className="rounded-xl flex items-center gap-1.5 text-gray-400 hover:text-gray-900 transition-all p-0 h-auto hover:bg-transparent"
                    >
                        <ChevronLeft size={20} />
                        <span className="text-sm font-bold sm:inline hidden">Settings</span>
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold">Suppliers</h1>
                        <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest mt-1.5 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                            Vendor Management
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                    <Button variant="outline" className="hidden sm:flex flex-initial rounded-2xl h-11 px-6 gap-2 border-gray-100 font-bold shadow-sm text-xs items-center hover:bg-gray-50 transition-all">
                        <Download size={16} />
                        Export
                    </Button>
                    <Button
                        onClick={() => { setSelectedSupplier(null); setIsAddModalOpen(true); }}
                        className="flex-1 sm:flex-initial items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl h-11 px-4 sm:px-6 font-bold shadow-lg shadow-slate-900/10 transition-all text-xs sm:text-sm"
                    >
                        <Plus size={18} />
                        <span className="whitespace-nowrap">Add Supplier</span>
                    </Button>
                </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-white p-4 sm:p-6 rounded-[24px] border border-gray-100 shadow-sm flex items-center gap-3 sm:gap-4 group hover:shadow-md transition-all duration-300">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-500 transition-transform group-hover:scale-110 duration-300 shrink-0">
                        <Truck size={18} className="sm:w-5 sm:h-5" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[9px] sm:text-xs font-bold text-gray-400 uppercase tracking-wider truncate">Total Suppliers</p>
                        <p className="text-lg sm:text-xl font-black text-gray-900 mt-0.5">{suppliers.length}</p>
                    </div>
                </div>
                <div className="bg-white p-4 sm:p-6 rounded-[24px] border border-gray-100 shadow-sm flex items-center gap-3 sm:gap-4 group hover:shadow-md transition-all duration-300">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-green-50 flex items-center justify-center text-green-500 transition-transform group-hover:scale-110 duration-300 shrink-0">
                        <CheckCircle2 size={18} className="sm:w-5 sm:h-5" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[9px] sm:text-xs font-bold text-gray-400 uppercase tracking-wider truncate">Active Vendors</p>
                        <p className="text-lg sm:text-xl font-black text-gray-900 mt-0.5">{suppliers.filter(s => s.isActive).length}</p>
                    </div>
                </div>
                <div className="bg-white p-4 sm:p-6 rounded-[24px] border border-gray-100 shadow-sm flex items-center gap-3 sm:gap-4 group hover:shadow-md transition-all duration-300">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-orange-50 flex items-center justify-center text-orange-500 transition-transform group-hover:scale-110 duration-300 shrink-0">
                        <Package size={18} className="sm:w-5 sm:h-5" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[9px] sm:text-xs font-bold text-gray-400 uppercase tracking-wider truncate">Supplied Items</p>
                        <p className="text-lg sm:text-xl font-black text-gray-900 mt-0.5">--</p>
                    </div>
                </div>
                <div className="bg-white p-4 sm:p-6 rounded-[24px] border border-gray-100 shadow-sm flex items-center gap-3 sm:gap-4 group hover:shadow-md transition-all duration-300">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-purple-50 flex items-center justify-center text-purple-500 transition-transform group-hover:scale-110 duration-300 shrink-0">
                        <Mail size={18} className="sm:w-5 sm:h-5" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[9px] sm:text-xs font-bold text-gray-400 uppercase tracking-wider truncate">Email Verified</p>
                        <p className="text-lg sm:text-xl font-black text-gray-900 mt-0.5">{suppliers.filter(s => s.email).length}</p>
                    </div>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="bg-white p-4 sm:p-5 rounded-[32px] border border-gray-100 shadow-sm space-y-3 sm:space-y-0 sm:flex sm:items-center sm:gap-4">
                <div className="flex-1 relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" size={20} />
                    <Input
                        placeholder="Search by company, contact..."
                        className="pl-12 rounded-[20px] border-gray-100 bg-gray-50/50 h-12 transition-all focus:bg-white focus:ring-4 focus:ring-blue-500/5 font-medium text-sm border-none shadow-inner"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
                <Button variant="outline" className="w-full sm:w-auto rounded-[18px] h-11 gap-2 border-gray-100 bg-gray-50/50 font-bold px-6 hover:bg-white text-xs text-gray-500 transition-all">
                    <Filter size={14} className="text-gray-400" />
                    All Statuses
                </Button>
            </div>

            {/* Loading State */}
            {loading && suppliers.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                    <Loader2 size={28} className="animate-spin text-blue-500" />
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Loading Vendors...</p>
                </div>
            )}

            {/* No Data State */}
            {filteredSuppliers.length === 0 && !loading && (
                <div className="bg-white rounded-[24px] sm:rounded-[32px] border border-gray-100 shadow-sm p-12 sm:p-20 flex flex-col items-center justify-center text-center animate-in fade-in zoom-in-95 duration-500">
                    <div className="w-20 h-20 bg-gray-50 rounded-[28px] flex items-center justify-center mb-6 shadow-inner">
                        <Truck size={36} className="text-gray-300" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-lg sm:text-xl font-black text-gray-900 mb-2">No Suppliers Found</h3>
                    <p className="text-xs sm:text-sm text-gray-400 max-w-xs mb-8 font-bold leading-relaxed">Try adjusting your search or add a new vendor to the system.</p>
                    <Button
                        onClick={() => { setSelectedSupplier(null); setIsAddModalOpen(true); }}
                        className="rounded-2xl h-12 bg-slate-900 hover:bg-slate-800 text-white gap-2 font-black shadow-lg shadow-slate-900/10 text-xs uppercase tracking-wider px-8 transition-all active:scale-95"
                    >
                        <Plus size={18} />
                        Add First Supplier
                    </Button>
                </div>
            )}

            {/* Mobile Card View */}
            {filteredSuppliers.length > 0 && (
                <div className="md:hidden space-y-4">
                    {filteredSuppliers.map((s) => (
                        <div key={s.id} className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
                            <div className="p-4 space-y-3">
                                {/* Header: Icon + Name + Badge */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-11 h-11 bg-blue-50 rounded-[14px] flex items-center justify-center text-blue-600 shrink-0">
                                            <Truck size={20} />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-sm font-black text-gray-900 truncate">{s.name}</h3>
                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                <div className={cn("w-1.5 h-1.5 rounded-full", s.isActive ? "bg-green-500" : "bg-gray-300")} />
                                                <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">{s.isActive ? 'Verified' : 'Inactive'}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <Badge className={cn(
                                        "rounded-full px-2 py-0.5 text-[8px] font-black uppercase tracking-wider shrink-0 border-0",
                                        s.isActive ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"
                                    )}>
                                        {s.isActive ? 'Active' : 'Inactive'}
                                    </Badge>
                                </div>

                                {/* Contact Person Chip */}
                                <div className="bg-gray-50/80 rounded-[16px] p-3 flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[9px] font-black shadow-sm border border-gray-100 text-blue-600 shrink-0">
                                        {s.contactName ? getInitials(s.contactName) : '??'}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-xs font-bold text-gray-800 leading-none truncate">
                                            {s.contactName || 'No contact'}
                                        </p>
                                        <p className="text-[8px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">Account Manager</p>
                                    </div>
                                </div>

                                {/* Contact Details Row */}
                                <div className="flex items-center gap-4 px-1 flex-wrap">
                                    {s.email && (
                                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400">
                                            <Mail size={11} className="text-blue-400" />
                                            <span className="truncate max-w-[120px]">{s.email}</span>
                                        </div>
                                    )}
                                    {s.phone && (
                                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400">
                                            <Phone size={11} className="text-green-400" />
                                            <span>{s.phone}</span>
                                        </div>
                                    )}
                                    {s.address && (
                                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400">
                                            <MapPin size={11} className="text-orange-400" />
                                            <span className="truncate max-w-[100px]">{s.address}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Mobile Action Bar */}
                            <div className="border-t border-gray-50 px-3 py-2 flex items-center justify-between bg-gray-50/30">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-3 rounded-lg text-[10px] font-bold text-gray-500 hover:text-blue-600 hover:bg-blue-50 gap-1.5"
                                    onClick={() => handleEdit(s)}
                                >
                                    <Edit size={12} />
                                    Edit Info
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 rounded-lg hover:bg-red-50 hover:text-red-600 text-gray-400 transition-colors"
                                    onClick={() => { setSelectedSupplier(s); setIsDeleteModalOpen(true); }}
                                >
                                    <Trash2 size={13} />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Desktop Suppliers Grid */}
            {filteredSuppliers.length > 0 && (
                <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-6">
                    {filteredSuppliers.map((s) => (
                        <div key={s.id} className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden group hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 flex flex-col h-full">
                            <div className="p-6 sm:p-8 space-y-6 flex-1">
                                {/* Card Header */}
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3 sm:gap-4">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 shadow-sm shadow-blue-500/10">
                                            <Truck size={20} className="sm:w-6 sm:h-6" />
                                        </div>
                                        <div className="max-w-[150px] sm:max-w-none">
                                            <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-tight truncate" title={s.name}>{s.name}</h3>
                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                <div className={cn("w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full", s.isActive ? "bg-green-500" : "bg-gray-300")} />
                                                <p className="text-[8px] sm:text-[10px] text-gray-400 font-bold uppercase tracking-wider">{s.isActive ? 'Verified Supplier' : 'Inactive'}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <Badge className={cn("rounded-lg px-2 sm:px-2.5 py-0.5 sm:py-1 text-[8px] sm:text-[10px] font-bold uppercase tracking-wider", s.isActive ? "bg-green-50 text-green-600 border-green-100" : "bg-gray-50 text-gray-400 border-gray-100")}>
                                        {s.isActive ? 'Active' : 'Inactive'}
                                    </Badge>
                                </div>

                                {/* Contact Person */}
                                <div className="bg-gray-50/50 rounded-2xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4 border border-gray-100/50 hover:bg-gray-50 transition-colors">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white flex items-center justify-center text-[8px] sm:text-[10px] font-black shadow-sm border border-gray-100 text-blue-600 shrink-0">
                                        {s.contactName ? getInitials(s.contactName) : '??'}
                                    </div>
                                    <div>
                                        <p className="text-xs sm:text-sm font-bold text-gray-800 leading-none mb-1">
                                            {s.contactName || 'No contact assigned'}
                                        </p>
                                        <div className="flex items-center gap-1">
                                            <User size={10} className="text-gray-400" />
                                            <p className="text-[8px] sm:text-[10px] text-gray-400 font-bold uppercase tracking-wider">Account Manager</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Info */}
                                <div className="space-y-3 px-1">
                                    <div className="flex items-center gap-3 group/info">
                                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400 group-hover/info:bg-blue-50 group-hover/info:text-blue-500 transition-colors shrink-0">
                                            <Mail size={12} className="sm:w-3.5 sm:h-3.5" />
                                        </div>
                                        <span className="text-[10px] sm:text-xs font-semibold text-gray-600 truncate flex-1" title={s.email}>{s.email || 'No email set'}</span>
                                    </div>
                                    <div className="flex items-center gap-3 group/info">
                                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400 group-hover/info:bg-blue-50 group-hover/info:text-blue-500 transition-colors shrink-0">
                                            <Phone size={12} className="sm:w-3.5 sm:h-3.5" />
                                        </div>
                                        <span className="text-[10px] sm:text-xs font-semibold text-gray-600">{s.phone || 'No phone set'}</span>
                                    </div>
                                    <div className="flex items-start gap-3 group/info">
                                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400 mt-0.5 group-hover/info:bg-blue-50 group-hover/info:text-blue-500 transition-colors shrink-0">
                                            <MapPin size={12} className="sm:w-3.5 sm:h-3.5" />
                                        </div>
                                        <span className="text-[10px] sm:text-xs font-semibold text-gray-600 leading-relaxed truncate-2-lines line-clamp-2" title={s.address}>
                                            {s.address || 'Address not listed'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="p-6 sm:p-8 pt-0 flex gap-2">
                                <Button
                                    variant="outline"
                                    className="flex-1 rounded-2xl h-10 sm:h-11 text-[10px] sm:text-xs font-bold gap-2 border-gray-100 shadow-sm hover:border-blue-100 hover:text-blue-600 transition-all hover:bg-blue-50/50"
                                    onClick={() => handleEdit(s)}
                                >
                                    <Edit size={14} />
                                    Edit Info
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-10 sm:w-11 rounded-2xl h-10 sm:h-11 text-xs font-bold border-gray-100 shadow-sm hover:border-red-100 hover:text-red-600 transition-all hover:bg-red-50/50 group/del"
                                    onClick={() => { setSelectedSupplier(s); setIsDeleteModalOpen(true); }}
                                >
                                    <Trash2 size={14} className="group-hover/del:scale-110 transition-transform" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <AddSupplierModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onRefresh={fetchData}
                supplier={selectedSupplier}
            />

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                title="Delete Supplier"
                message={`Are you sure you want to delete ${selectedSupplier?.name}? This action cannot be undone and will remove all contact information associated with this vendor.`}
            />
        </div>
    );
};

// --- Delete Confirm Modal ---
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title, message }: any) => {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-sm rounded-[32px] p-6 sm:p-8 overflow-hidden border-none shadow-2xl animate-in fade-in zoom-in-95">
                <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center text-red-500 mb-2">
                        <AlertTriangle size={42} strokeWidth={1.5} />
                    </div>
                    <div>
                        <h3 className="text-xl font-black text-gray-900">{title}</h3>
                        <p className="text-sm text-gray-500 mt-2 font-bold leading-relaxed px-2 tracking-tight opacity-70">{message}</p>
                    </div>
                </div>
                <div className="flex gap-3 mt-8">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="flex-1 rounded-2xl h-14 font-black text-[11px] uppercase tracking-wider border-gray-100 hover:bg-gray-50 transition-all"
                    >
                        Keep Vendor
                    </Button>
                    <Button
                        onClick={onConfirm}
                        className="flex-1 rounded-2xl h-14 bg-red-600 hover:bg-red-700 text-white font-black text-[11px] uppercase tracking-wider shadow-lg shadow-red-600/20 transition-all active:scale-95"
                    >
                        Confirm Delete
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default Suppliers;
